import { useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { ArrowLeft } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const TERMS_TEXT = `End-User Terms of Use and Service Agreement

IMPORTANT NOTICE TO THE USER: Please read this Agreement carefully before registering on the web platform or accessing any location or property valuation tools. By completing the registration process, checking the "I Accept" box, or purchasing credit tokens, you (the Licensee) acknowledge that you have read, understood, and bound yourself to the terms and conditions set out below. If you do not agree to these terms, you must immediately cease usage of the web platform.

Parties to the Agreement

This Agreement is entered into by and between:

1. THE LICENSOR:
  Beaconwood Partners (Pty) Ltd
  Registration No: 2014/154305/07
  Address: 243 Crested Barbet Avenue, Seasons Lifestyle Estate, Brits, 0250
  Email: info@beaconwoodpartners.com

2. THE LICENSEE:
  The individual or corporate entity whose details are completed during the digital registration process on the web platform.

1. Definitions and Interpretation

In this Agreement, the following terms shall bear the meanings assigned to them:

"AVM" means the Automated Valuation Model, a proprietary algorithmic engine used to generate location suitability parameters for spaza shops and estimated market values of immovable property.

"Credits" means the prepaid digital tokens purchased by the Licensee via the web platform, which serve as the exclusive currency required to generate Reports or extract valuation insights.

"Effective Date" means the date upon which the Licensee successfully registers an account and accepts these terms.

"Intellectual Property Rights" means all patents, trademarks, service marks, design rights, copyright, trade secrets, software code, algorithms, database rights, and know-how embedded within the platform.

"Reports" means any downloadable, viewable, or exportable outputs, mapping layers, location profiles, or property value matrices generated by the platform interface. A single valuation transaction generates a bundled report set containing both a Property Valuation Report and a Site Valuation Report.

"Third-Party Supplier" means external bodies supplying core underlying datasets, including but not limited to the South African Deeds Office, Surveyor-General's Office, local municipal GIS systems, and commercial demographic telemetry suppliers.

2. Scope of License and Named User Policy

2.1 Grant of License
The Licensor hereby grants the Licensee a personal, non-exclusive, non-transferable, revocable, and limited license to access the web platform and use the geographical valuation tools strictly for internal commercial or planning purposes.

2.2 Restrictions on Use
The Licensee shall not reverse-engineer, decompile, copy, adapt, modify, or scrape any data from the web platform. The use of automated extraction tools, bots, or programmatic web scrapers is strictly prohibited.

2.3 Named User Policy
Access credentials (usernames and passwords) are issued strictly to specified individuals. Sharing access credentials with external parties, unlisted business colleagues, or across multiple corporate branches constitutes a material breach of this contract.

2.4 Credential Security
The Licensee remains fully liable for all data transactions and costs incurred under their login profile. The Licensor must be notified immediately if any credentials are compromised or if an authorized staff member leaves the company.

3. Prepaid Fees, Credit Framework and Tax Invoicing

3.1 Pay-As-You-Go Credit Framework
The web platform operates strictly on a pay-as-you-go commercial basis using prepaid Credit tokens. The billing mechanics are defined as follows:

(a) The Licensee is required to top up their account balance by purchasing Credits in advance via the single report or credit pack options detailed in Annexure A (Fee Schedule).

(b) Minimum Purchase Floor: The absolute minimum transaction baseline required to utilize the tool is one (1) Credit, financially equivalent to a single once-off valuation report purchase of R250.00 (excl. VAT), being R287.50 (incl. VAT at 15%).

(c) The consumption of Credits occurs in real-time, with specific unit deductions applied per valuation transaction. Each successful deduction yields a comprehensive report set comprising a Property Valuation Report and a Site Valuation Report.

(d) Access to report generation and valuation engines is strictly conditional upon a positive Credit balance. The Licensee cannot generate any reports or extract valuation insights if their account balance is zero or insufficient.

3.2 Payment Method: Manual EFT (Electronic Funds Transfer)
Credits are purchased by depositing the applicable amount via direct bank transfer (EFT) into the Licensor's designated business account. Upon receipt and verification of payment by the Licensor, Credits will be manually allocated to the Licensee's platform account. The Licensor will endeavour to process and allocate Credits within one (1) business day of receiving cleared funds.

Banking Details for EFT Payments:
  Account Name: Beaconwood Partners (Pty) Ltd
  Bank: First National Bank (FNB)
  Account Number: 62-836-224-965
  Branch Code: 250655
  Reference: Your company name

Note: An integrated online payment gateway (credit card, debit card, instant EFT) will be made available in a future platform update. Users will be notified when this facility becomes active.

3.3 Payment Confirmation and Credit Allocation
(a) Upon making an EFT payment, the Licensee must email proof of payment to info@beaconwoodpartners.com with the subject line "Credit Purchase - [Your Email]".
(b) The Licensor will verify the payment against the business bank account and allocate the corresponding Credits to the Licensee's account.
(c) The Licensee will receive a confirmation notification on the platform once Credits have been allocated.

3.4 Electronic VAT Invoicing
Simultaneously with the processing of any successful transaction or payment, the Licensor shall issue an electronic Tax Invoice to the Licensee's registered email address. This invoice shall be fully compliant with South African Revenue Service (SARS) requirements, explicitly calculating VAT at the prevailing statutory rate. It is the Licensee's sole responsibility to ensure that their VAT information, company registration details, and billing email addresses are kept current on the platform dashboard.

3.5 No-Refund and Credit Validity Window (CPA Disclosure)
(a) The Licensor is under no obligation to refund a user's purchased Credits for any reason whatsoever.
(b) Explicit Token Expiry Agreement: To the extent permissible under Section 63 of the Consumer Protection Act, Act 68 of 2008 (CPA), all Credits purchased on the platform are structured as limited-duration service access tokens that expire exactly two (2) months from their allocation date. Any unused balance remaining after this two-month window will be automatically forfeited without further notice.

4. Agreement Term and Profile Termination

4.1 Pure Pay-As-You-Go Nature
Because the platform operates strictly on a prepaid, transactional credit mechanism with no mandatory fixed-term monthly subscription, there is no initial minimum commitment period for manual once-off transactions.

4.2 Account Closure
To close an account, the Licensee must submit a written request via email to the specified support address. Unspent credits remaining on an account at the time of voluntary account closure are subject to Section 3.5 and will not be refunded.

5. Website Usage, System Integrity and Security

5.1 Platform Accessibility
Access to the valuation tools and features is provided exclusively via the web portal interface (website). The Licensor does not provide any programmatic, raw API, or backend server-to-server integration under this standard agreement.

5.2 Rate Limiting and Abuse Prevention
The Licensee must interact with the website manually through standard web browsers. The Licensor reserves the right to implement automatic volume throttles and session limitations.

5.3 Account and Portal Security
All user login profiles are intended for browser-based entry only. Hardcoding, embedding, or injecting platform login credentials, session tokens, or browser cookies into external applications is strictly prohibited and constitutes a material breach of security.

5.4 Prohibition on Commercial Reselling
The website portal is provided solely for the Licensee's internal business workflow. Displaying the live web interface within external frames (i-framing), repackaging data to build a competing product, or using browser automation tools to feed downstream dashboards is an immediate cause for account termination without notice.

6. Protection of Personal Information Act (POPIA) Compliance

6.1 Warranties by Licensee
The Licensee expressly warrants that they are operating in strict compliance with the Protection of Personal Information Act, Act 4 of 2013 (POPIA).

6.2 Legal Basis for Queries
The Licensee certifies that they possess a lawful justification or a legitimate business interest prior to executing queries that pull identifiable personal datasets through the platform. The Licensee fully indemnifies and holds the Licensor harmless against any regulatory fines or civil actions brought by the Information Regulator.

6.3 Anonymised Aggregation
The Licensee agrees that the Licensor may capture, aggregate, and process anonymised, de-identified transactional inputs to compile market indices, optimise model accuracy, and generate public market intelligence updates.

7. Dependency on Third-Party Data Suppliers

7.1 Data Sourcing Boundaries
The Licensee acknowledges that the platform's geographical scores, property boundaries, and valuations are heavily reliant on databases maintained by Third-Party Suppliers.

7.2 No Warranty on External Records
The Licensor provides no warranty regarding the accuracy, completeness, or timeliness of records retrieved from external public registries.

7.3 Exclusion of Delays
The Licensor shall not be held liable for service interruptions or delayed report generation arising from Third-Party Supplier outages or system failures.

8. Absolute Limitation of Liability

8.1 Desktop Approximations Only
The AVM engine and geographical reports are intended as desktop-based statistical approximations for general guidance and commercial scoping only. They do not constitute formal bank-grade valuations, structural engineering assessments, or professional real estate appraisals.

8.2 Total Exclusion of Financial Claims
Under no circumstances shall the Licensor, its directors, employees, or affiliates be held liable for any direct, indirect, incidental, special, punitive, or consequential financial damages whatsoever.

8.3 Exempted Losses
This exclusion covers any financial losses relating to failed spaza shop investments, inaccurate projections, real estate transaction failures, property acquisition overpayments, or lost profits stemming from reliance on platform data.

8.4 Sole Technical Remedy
The Licensee's sole remedy for platform errors is requesting that the Licensor investigate, correct, or regenerate the disputed report data, free of charge. No monetary refunds or cash compensations will be entertained.

9. Maintenance, Upgrades and Force Majeure

9.1 System Upgrades
The Licensor shall maintain and upgrade the platform systems at its discretion and cost.

9.2 Force Majeure
Neither party shall be liable for failure to fulfill obligations (excluding payment) caused by events beyond reasonable control, including load shedding, internet outages, civil unrest, or changes in national data regulations.

10. Governing Law and Jurisdiction

This Agreement shall be governed exclusively by the laws of the Republic of South Africa. Both parties consent to the jurisdiction of the High Court of South Africa regarding any formal legal dispute arising from this contract.

Digital Acceptance and Execution Clause

By checking the box marked "I have read, understood, and accept the Terms of Use and Service Agreement" during the onboarding process, the Licensee provides their advanced electronic signature as defined by the Electronic Communications and Transactions Act, Act 25 of 2002 (ECTA). This action establishes a fully binding, enforceable contract between the Licensee and the Licensor.

Annexure A: Fee Schedule

All amounts listed exclude VAT and are subject to change per Section 3:

  Once-Off Report (1 credit)       R 250.00 (R 287.50 incl. VAT)    Valid 2 months
  Credit Pack Bundle (10 reports)  R 2,500.00 (R 2,875.00 incl. VAT) Valid 2 months
  Late Payment Admin Fee           R 100.00     Per occurrence`;

export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registerStep, setRegisterStep] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'register' && registerStep === 1) {
      if (!name.trim()) { setError('Please enter your name'); return; }
      if (!email.trim()) { setError('Please enter your email'); return; }
      if (!password || password.length < 6) { setError('Password must be at least 6 characters'); return; }
      setRegisterStep(2);
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setRegisterStep(1);
    setTermsAccepted(false);
    setForgotMode(false);
    setForgotMsg('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { setError('Please enter your email address'); return; }
    setForgotLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotMsg(data.message || 'Request sent to administrator.');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Failed to send request');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 style={{fontFamily: "'Poppins', sans-serif", fontSize: '8px', fontWeight: 700, letterSpacing: '0.3em', color: '#ffffff', marginBottom: '8px'}}>{"N K O S I ' S"}</h1>
          <p className="text-slate-400 text-sm">Automated Valuation Model</p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8" data-testid="auth-form">
          <div className="flex mb-6 border-b border-slate-200">
            <button
              onClick={() => switchMode('login')}
              className={`flex-1 pb-3 text-sm font-semibold transition-colors ${mode === 'login' ? 'text-slate-800 border-b-2 border-slate-800' : 'text-slate-400'}`}
              data-testid="tab-login"
            >
              Sign In
            </button>
            <button
              onClick={() => switchMode('register')}
              className={`flex-1 pb-3 text-sm font-semibold transition-colors ${mode === 'register' ? 'text-slate-800 border-b-2 border-slate-800' : 'text-slate-400'}`}
              data-testid="tab-register"
            >
              Create Account
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded mb-4" data-testid="auth-error">
              {error}
            </div>
          )}

          {/* LOGIN FORM */}
          {mode === 'login' && !forgotMode && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                  placeholder="you@example.com" data-testid="email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Password</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                  placeholder="••••••••" data-testid="password-input"
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-2.5 bg-slate-800 text-white text-sm font-semibold rounded-md hover:bg-slate-700 disabled:opacity-50 transition-colors"
                data-testid="auth-submit-btn"
              >
                {loading ? 'Please wait...' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={() => { setForgotMode(true); setError(''); setForgotMsg(''); setForgotEmail(email); }}
                className="w-full text-xs text-slate-500 hover:text-slate-800 transition-colors py-1"
                data-testid="forgot-password-btn"
              >
                Forgot password?
              </button>
            </form>
          )}

          {/* FORGOT PASSWORD */}
          {mode === 'login' && forgotMode && (
            <div className="space-y-4" data-testid="forgot-password-form">
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => { setForgotMode(false); setError(''); setForgotMsg(''); }}
                  className="text-slate-400 hover:text-slate-700 transition-colors"
                  data-testid="forgot-back-btn"
                >
                  <ArrowLeft size={18} />
                </button>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Reset Password</span>
              </div>
              {forgotMsg ? (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded" data-testid="forgot-success-msg">
                  {forgotMsg}
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-xs text-slate-500">Enter your email address. A password reset request will be sent to the administrator.</p>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Email</label>
                    <input
                      type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                      placeholder="you@example.com" data-testid="forgot-email-input"
                    />
                  </div>
                  <button type="submit" disabled={forgotLoading}
                    className="w-full py-2.5 bg-slate-800 text-white text-sm font-semibold rounded-md hover:bg-slate-700 disabled:opacity-50 transition-colors"
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? 'Sending...' : 'Request Password Reset'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* REGISTER STEP 1: DETAILS */}
          {mode === 'register' && registerStep === 1 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Full Name</label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                  placeholder="Your name" data-testid="register-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                  placeholder="you@example.com" data-testid="email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Password</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                  placeholder="••••••••" data-testid="password-input"
                />
              </div>
              <button type="submit"
                className="w-full py-2.5 bg-slate-800 text-white text-sm font-semibold rounded-md hover:bg-slate-700 transition-colors"
                data-testid="register-next-btn"
              >
                Next — Review Terms
              </button>
            </form>
          )}

          {/* REGISTER STEP 2: TERMS ACCEPTANCE */}
          {mode === 'register' && registerStep === 2 && (
            <div className="space-y-4" data-testid="terms-step">
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => { setRegisterStep(1); setTermsAccepted(false); }}
                  className="text-slate-400 hover:text-slate-700 transition-colors"
                  data-testid="terms-back-btn"
                >
                  <ArrowLeft size={18} />
                </button>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Step 2 of 2 — Terms of Service</span>
              </div>

              <div
                className="border border-slate-300 rounded-md bg-slate-50 overflow-y-auto"
                style={{ height: '280px', padding: '16px' }}
                data-testid="terms-scrollable-box"
              >
                <div style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  fontFamily: "'Inter', 'Segoe UI', sans-serif",
                  fontSize: '11px',
                  lineHeight: '1.7',
                  color: '#334155',
                  margin: 0,
                }}>
                  {TERMS_TEXT}
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 border-2 border-slate-400 rounded accent-slate-800 cursor-pointer flex-shrink-0"
                  data-testid="terms-checkbox"
                />
                <span className="text-xs text-slate-700 leading-relaxed">
                  I have read, understood, and accept the <strong>End-User Terms of Use and Service Agreement</strong>.
                </span>
              </label>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!termsAccepted || loading}
                className={`w-full py-2.5 text-sm font-semibold rounded-md transition-colors ${
                  termsAccepted
                    ? 'bg-slate-800 text-white hover:bg-slate-700 cursor-pointer'
                    : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                }`}
                data-testid="complete-registration-btn"
              >
                {loading ? 'Creating account...' : 'Complete Registration'}
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-slate-500 text-xs mt-6">
          Powered by Nkosi{"'"}s Analytics — Beaconwood Partners (Pty) Ltd
        </p>
      </div>
    </div>
  );
}
